import { createContext, ReactNode, useContext, useMemo, useReducer } from 'react';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { theme } from 'twin.macro';

import { unimplemented } from '@utils/commonUtils';
import { getDateNumberDaysAgo } from '@utils/dateUtils';
import { AQLQuery, AQLQueryValue } from '@insights_module/adc_ql/types';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLCompositeQuery,
  AQLContextualFilterQuery,
  AQLContextualQuery,
  AQLPublishedDateQuery,
} from '@insights_module/adc_ql/queries';
import { AudiosQueryObject, ISearchAudioResponse } from '@insights_module/types/Episode';
import { adcApi } from '@configs/axios-instances';
import { GARM_CATEGORIES_ARRAY } from '@insights_module/constants/garm';

const queryFunction = async ({
  pageParam = 1,
  pageSize = 1,
  query,
}: {
  pageParam?: number;
  pageSize?: number;
  query: AudiosQueryObject;
}) => {
  const res: ISearchAudioResponse = await adcApi.POST.post(
    `/audio/search?page_no=${pageParam}&page_size=${pageSize}`,
    { query }
  );

  const { data, pagination } = res;

  return { data, pagination, pageParam };
};

type OverviewContextKeys =
  | 'insights.overview.selected_date_range'
  | 'insights.overview.selected_trending_date_range'
  | 'insights.overview.entities_search_term'
  | 'insights.overview.entities_selected_type'
  | 'insights.overview.contextual_search_term'
  | 'insights.overview.selected_brand_safety_category'
  | 'insights.overview.episodes_list_modal_data';

const OVERVIEW_CONTEXT_KEYS = {
  SELECTED_DATE_RANGE: 'insights.overview.selected_date_range' as OverviewContextKeys,
  SELECTED_TRENDING_DATE_RANGE:
    'insights.overview.selected_trending_date_range' as OverviewContextKeys,
  ENTITIES_SEARCH_TERM: 'insights.overview.entities_search_term' as OverviewContextKeys,
  ENTITIES_SELECTED_TYPE: 'insights.overview.entities_selected_type' as OverviewContextKeys,
  CONTEXTUAL_SEARCH_TERM: 'insights.overview.contextual_search_term' as OverviewContextKeys,
  SELECTED_BRAND_SAFETY_CATEGORY:
    'insights.overview.selected_brand_safety_category' as OverviewContextKeys,
  EPISODES_LIST_MODAL_DATA: 'insights.overview.episodes_list_modal_data' as OverviewContextKeys,
};

type Action = {
  type: OverviewContextKeys;
  payload: number | string | string[] | boolean | null | DateRange | EpisodesListModalData;
};

export type DateRange = {
  description: DateDescriptions;
  minDate: string | null;
  maxDate: string | null;
  days?: number;
};

export type DateDescriptions =
  | 'last_7_days'
  | 'last_30_days'
  | 'last_60_days'
  | 'last_90_days'
  | 'all_time';

export type EpisodesListModalData = {
  category: string;
  riskLevel: string;
};

export interface OverviewContext {
  selectedDateRange: DateRange;
  selectedTrendingDateRange: DateRange;
  entitiesSearchTerm: string;
  selectedEntitiesType: string;
  contextualSearchTerm: string;
  episodesCount: number;
  isEpisodeCountLoading: boolean;
  trendingEpisodesCount: number;
  isTrendingEpisodeCountLoading: boolean;
  aqlQueries: {
    namedEntitiesQuery: { all: AQLQueryValue[] };
    contextualQuery: { all: AQLQueryValue[] };
    brandSafetyQuery: { all: AQLQueryValue[] };
    brandSafetyGarmRiskOverviewQuery: { all: AQLQueryValue[] };
  };
  trendingAqlQueries: {
    brandSafetyGarmRiskOverviewQuery: { all: AQLQueryValue[] };
  };
  selectedBrandSafetyCategory: string;
  episodesListModalData: EpisodesListModalData | null;
  setSelectedDateRange: (value: DateRange) => void;
  setSelectedTrendingDateRange: (value: DateRange) => void;
  setEntitiesSearchTerm: (value: string) => void;
  setSelectedEntitiesType: (value: string) => void;
  setContextualSearchTerm: (value: string) => void;
  setSelectedBrandSafetyCategory: (value: string) => void;
  setEpisodesListModalData: (value: EpisodesListModalData | null) => void;
}

const initialState: OverviewContext = {
  selectedDateRange: {
    minDate: getDateNumberDaysAgo(7),
    maxDate: new Date().toISOString(),
    description: 'last_7_days',
  },
  selectedTrendingDateRange: {
    minDate: getDateNumberDaysAgo(14),
    maxDate: getDateNumberDaysAgo(7),
    description: 'last_7_days',
    days: 7,
  },
  entitiesSearchTerm: '',
  selectedEntitiesType: 'ORG',
  contextualSearchTerm: '',
  aqlQueries: {
    namedEntitiesQuery: { all: [] },
    contextualQuery: { all: [] },
    brandSafetyQuery: { all: [] },
    brandSafetyGarmRiskOverviewQuery: { all: [] },
  },
  trendingAqlQueries: {
    brandSafetyGarmRiskOverviewQuery: { all: [] },
  },
  episodesCount: 0,
  isEpisodeCountLoading: false,
  trendingEpisodesCount: 0,
  isTrendingEpisodeCountLoading: false,
  episodesListModalData: null,
  selectedBrandSafetyCategory: '',
  setSelectedDateRange: unimplemented,
  setSelectedTrendingDateRange: unimplemented,
  setEntitiesSearchTerm: unimplemented,
  setSelectedEntitiesType: unimplemented,
  setContextualSearchTerm: unimplemented,
  setSelectedBrandSafetyCategory: unimplemented,
  setEpisodesListModalData: unimplemented,
};

const reducer = produce((draft: OverviewContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case OVERVIEW_CONTEXT_KEYS.SELECTED_DATE_RANGE:
      draft.selectedDateRange = payload as DateRange;
      break;

    case OVERVIEW_CONTEXT_KEYS.SELECTED_TRENDING_DATE_RANGE:
      draft.selectedTrendingDateRange = payload as DateRange;
      break;

    case OVERVIEW_CONTEXT_KEYS.ENTITIES_SEARCH_TERM:
      draft.entitiesSearchTerm = payload as string;
      break;

    case OVERVIEW_CONTEXT_KEYS.ENTITIES_SELECTED_TYPE:
      draft.selectedEntitiesType = payload as string;
      break;

    case OVERVIEW_CONTEXT_KEYS.CONTEXTUAL_SEARCH_TERM:
      draft.contextualSearchTerm = payload as string;
      break;

    case OVERVIEW_CONTEXT_KEYS.SELECTED_BRAND_SAFETY_CATEGORY:
      draft.selectedBrandSafetyCategory = payload as string;
      break;

    case OVERVIEW_CONTEXT_KEYS.EPISODES_LIST_MODAL_DATA:
      draft.episodesListModalData = payload as EpisodesListModalData | null;
      break;

    default:
      break;
  }
}, initialState);

export const OverviewContext = createContext<OverviewContext>(initialState);

export const OverviewProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { selectedDateRange, selectedTrendingDateRange } = state;

  const aqlQueries = useMemo(() => {
    const queries = {
      totalEpisodesCountQuery: {
        all: [
          new AQLBrandSafetyGarmQuery([
            new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
              gte: 'unrelated',
              lte: 'floor',
            }),
          ]),
        ] as AQLQueryValue[],
      },
      namedEntitiesQuery: { all: [] as AQLQueryValue[] },
      contextualQuery: {
        all: [
          new AQLContextualQuery('includes', [
            new AQLContextualFilterQuery('taxonomy', 'eq', 'iab'),
          ]),
        ] as AQLQueryValue[],
      },
      brandSafetyQuery: { all: [] as AQLQueryValue[] },
      brandSafetyGarmRiskOverviewQuery: { all: [] as AQLQueryValue[] },
    };

    if (
      selectedDateRange.description !== 'all_time' &&
      selectedDateRange.minDate &&
      selectedDateRange.maxDate
    ) {
      queries.totalEpisodesCountQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
      queries.namedEntitiesQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
      queries.contextualQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
      queries.brandSafetyQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
      queries.brandSafetyGarmRiskOverviewQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedDateRange.minDate,
          lte: selectedDateRange.maxDate,
        })
      );
    }

    queries.brandSafetyGarmRiskOverviewQuery.all.push(
      new AQLCompositeQuery('any', [
        ...GARM_CATEGORIES_ARRAY.map(
          (category) =>
            new AQLBrandSafetyGarmQuery([
              new AQLBrandSafetyGarmFilterQuery(category, 'range', { gte: 'high', lte: 'floor' }),
            ]) as AQLQuery
        ),
      ])
    );

    return queries;
  }, [selectedDateRange]);

  const trendingAqlQueries = useMemo(() => {
    const queries = {
      totalEpisodesCountQuery: {
        all: [
          new AQLBrandSafetyGarmQuery([
            new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
              gte: 'unrelated',
              lte: 'floor',
            }),
          ]),
        ] as AQLQueryValue[],
      },
      brandSafetyGarmRiskOverviewQuery: { all: [] as AQLQueryValue[] },
    };

    if (
      selectedTrendingDateRange.description !== 'all_time' &&
      selectedTrendingDateRange.minDate &&
      selectedTrendingDateRange.maxDate
    ) {
      queries.totalEpisodesCountQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedTrendingDateRange.minDate,
          lte: selectedTrendingDateRange.maxDate,
        })
      );
      queries.brandSafetyGarmRiskOverviewQuery.all.push(
        new AQLPublishedDateQuery('range', {
          gte: selectedTrendingDateRange.minDate,
          lte: selectedTrendingDateRange.maxDate,
        })
      );
    }

    queries.brandSafetyGarmRiskOverviewQuery.all.push(
      new AQLCompositeQuery('any', [
        ...GARM_CATEGORIES_ARRAY.map(
          (category) =>
            new AQLBrandSafetyGarmQuery([
              new AQLBrandSafetyGarmFilterQuery(category, 'range', { gte: 'high', lte: 'floor' }),
            ]) as AQLQuery
        ),
      ])
    );

    return queries;
  }, [selectedTrendingDateRange]);

  const { data, isLoading: isEpisodeCountLoading } = useQuery({
    queryKey: ['overview', 'period-episode-count', selectedDateRange.description],
    queryFn: () => queryFunction({ query: aqlQueries.totalEpisodesCountQuery }),
    enabled: !!selectedDateRange,
    staleTime: 1000 * 60 * 30,
  });

  const { data: trendingData, isLoading: isTrendingEpisodeCountLoading } = useQuery({
    queryKey: ['overview', 'trending-period-episode-count', selectedTrendingDateRange.description],
    queryFn: () => queryFunction({ query: trendingAqlQueries.totalEpisodesCountQuery }),
    enabled: selectedTrendingDateRange.description !== 'all_time',
    staleTime: 1000 * 60 * 30,
  });

  const episodesCount = useMemo(() => data?.pagination?.total || 0, [data]);

  const trendingEpisodesCount = useMemo(() => trendingData?.pagination?.total || 0, [trendingData]);

  const setSelectedDateRange = (value: DateRange) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.SELECTED_DATE_RANGE, payload: value });
  };

  const setSelectedTrendingDateRange = (value: DateRange) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.SELECTED_TRENDING_DATE_RANGE, payload: value });
  };

  const setEntitiesSearchTerm = (value: string) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.ENTITIES_SEARCH_TERM, payload: value });
  };

  const setSelectedEntitiesType = (value: string) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.ENTITIES_SELECTED_TYPE, payload: value });
  };

  const setContextualSearchTerm = (value: string) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.CONTEXTUAL_SEARCH_TERM, payload: value });
  };

  const setSelectedBrandSafetyCategory = (value: string) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.SELECTED_BRAND_SAFETY_CATEGORY, payload: value });
  };

  const setEpisodesListModalData = (value: EpisodesListModalData | null) => {
    dispatch({ type: OVERVIEW_CONTEXT_KEYS.EPISODES_LIST_MODAL_DATA, payload: value });
  };

  const value = {
    ...state,
    setSelectedDateRange,
    setSelectedTrendingDateRange,
    setEntitiesSearchTerm,
    setSelectedEntitiesType,
    setContextualSearchTerm,
    aqlQueries,
    trendingAqlQueries,
    episodesCount,
    isEpisodeCountLoading,
    trendingEpisodesCount,
    isTrendingEpisodeCountLoading,
    setSelectedBrandSafetyCategory,
    setEpisodesListModalData,
  };

  return <OverviewContext.Provider value={value}>{children}</OverviewContext.Provider>;
};

export const useOverviewContext = () => {
  return useContext(OverviewContext);
};

export const GARM_RISK_LEVEL_COLOR_MAPPER = {
  unrelated: theme`colors.garm-unrelated-bg`,
  low: theme`colors.garm-low-bg`,
  medium: theme`colors.garm-medium-bg`,
  high: theme`colors.garm-high-bg`,
  floor: theme`colors.floor-bg`,
};

export const IAB_RISK_LEVEL_COLOR_MAPPER = {
  unrelated: theme`colors.garm-unrelated-bg`,
  likely_unrelated: theme`colors.garm-low-bg`,
  likely_related: theme`colors.garm-medium-bg`,
  related: theme`colors.garm-high-bg`,
};

export const IAB_RISK_LEVEL_NAME_MAPPER = {
  unrelated: 'unrelated',
  likely_unrelated: 'likely unrelated',
  likely_related: 'likely related',
  related: 'related',
};
